import { useCallback, type ReactNode } from 'react';

import { Badge, type BadgeTone } from '@/shared/ui/badge';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The tenant's ENTITLEMENTS — the capability grants it holds.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE TENANT HAS, NOT WHAT THE USER MAY DO
 * ---------------------------------------------------------------------------
 * An entitlement is what the TENANT has been given; a permission is what the ACTOR may do.
 * They are different things and the dashboard keeps them apart: this widget shows the grants
 * the tenant holds, with their scope, their state and whether they are usable.
 *
 * `state` and `usable` are both shown, because they are not the same: a grant can be in hand
 * and not yet usable (a future effective date), and one that is not usable is worth knowing
 * about before a feature is assumed to be there.
 * ---------------------------------------------------------------------------------------------- */
const STATE_TONE: Record<string, BadgeTone> = {
  active: 'positive',
  pending: 'caution',
  revoked: 'critical',
  expired: 'neutral',
};

export function EntitlementsSummaryWidget({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.entitlements(), []);

  return (
    <DashboardWidgetFrame
      title="Entitlements"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No entitlements"
      emptyDescription="This tenant holds no capability grants yet."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <StatCard
              label="Grants held"
              value={String(data.total)}
              caption="Capability entitlements for this tenant"
            />
            <StatCard
              label="Usable now"
              value={String(data.usable)}
              tone={data.usable === 0 ? 'warning' : 'positive'}
            />
          </div>

          <ul className="flex flex-col">
            {data.entitlements.map((entitlement) => (
              <li
                key={`${entitlement.capability}-${entitlement.scopeType}`}
                className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-content">{entitlement.capability}</span>
                  <span className="text-xs text-content-muted">
                    scope: {entitlement.scopeType}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge tone={STATE_TONE[entitlement.state] ?? 'neutral'}>{entitlement.state}</Badge>
                  {!entitlement.usable ? (
                    <Badge tone="caution">Not usable yet</Badge>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
